// Shared with mju-aioe/packages/shared/src/schemas/workbenchBackup.ts — keep the two identical.
/**
 * A person's **workbench backup**: their desktop settings, their projects and
 * repositories described so they can be found again on another machine (git
 * remote, branch, project manifest ids, the kind and name of the machine the
 * repository was on, the path it had there), and each repository's workbench
 * data — chat history, runs and, in a local backup only, transcripts and the
 * audit trail. Written by AI Workbench to a file on this computer and, when the
 * person is signed in to an organisation that keeps backups, to the
 * organisation (AIOE), so a new desktop can be restored after sign-in.
 * Schema version {@link WORKBENCH_BACKUP_VERSION}.
 *
 * Routes (AIOE). Every route is scoped to the PERSON the bearer token belongs
 * to; there is no person id in the path, so a desktop can only ever read or
 * write its own person's backup:
 *
 *   PUT    /people/v1/me/workbench-backup           WorkbenchBackup, scope "organisation" → 200 WorkbenchBackupStored
 *            body at most MAX_ORGANISATION_BACKUP_BYTES (16 MiB) of UTF-8 JSON → else 413
 *            not a backup, scope not "organisation", or a version the server does not know → 422
 *            the organisation does not keep backups for this person (policy, licence) → 403
 *   GET    /people/v1/me/workbench-backup           → 200 { backup: WorkbenchBackup, ...WorkbenchBackupStored } · 404 none kept
 *   GET    /people/v1/me/workbench-backup/summary   → 200 WorkbenchBackupSummary · 404 none kept
 *   DELETE /people/v1/me/workbench-backup           → 204 (the person; an administrator from the console)
 *
 * Rules both sides keep:
 *
 * - **Scoped to the person.** One backup per person per organisation; a PUT
 *   replaces it and bumps `revision`. A token that is a person's, or a
 *   desktop's that belongs to a person, may read and write that person's
 *   backup only. A node's token, or a device with no person, gets 403.
 * - **Never secrets.** The workbench leaves out provider keys, tokens, the
 *   secret vault, the host's token files and every settings key that could
 *   carry a credential (see {@link isSecretSettingKey}), and strips any user
 *   name and password from remote URLs ({@link redactRemoteUrl}). AIOE runs its
 *   credential masking over every string on arrival as a second line, as it
 *   does for memory.
 * - **Small.** The organisation copy carries text files only (`encoding:
 *   "utf8"`), from {@link ORGANISATION_FILE_AREAS} — chat history, runs and the
 *   repository's state file — newest first until it fits; transcripts and the
 *   audit trail stay on the computer. The size limit is checked on the encoded
 *   request body.
 * - **Encrypted at rest** with the tenant's key; the console shows the summary
 *   (when, from which device, how big) and a delete action, never the content.
 * - **Kept** until replaced or deleted, or until the person leaves the
 *   organisation.
 * - **Versioned.** A server answers 422 to a version it does not know; a
 *   client refuses to restore a version above the one it knows.
 *
 * The local backup file has the same shape (scope "local"), gzip-compressed;
 * the desktop also reads the older settings-only file
 * (`kind: "mju-workbench-settings"`).
 *
 * Words for people: back up your workbench, restore from a backup, kept by
 * your organisation, this repository moved.
 */
import { z } from "zod";
export const WORKBENCH_BACKUP_KIND = "mojoup-workbench-backup";
export const WORKBENCH_BACKUP_VERSION = 1;
/** The largest PUT body an organisation accepts (UTF-8 JSON). */
export const MAX_ORGANISATION_BACKUP_BYTES = 16 * 1024 * 1024;
/** The largest single file a backup carries; a bigger one is listed as skipped. */
export const MAX_BACKUP_FILE_BYTES = 32 * 1024 * 1024;
/**
 * The parts of a repository's workbench storage a backup reads, relative to
 * it. `facilitator` is the chat history, `sessions` the transcripts, `runs`
 * the work timeline, `audit` the audit trail, `workspace-state.json` the
 * repository's own state. Nothing else is read: the host's token files live
 * beside these and never leave the machine.
 */
export const BACKUP_FILE_AREAS = ["facilitator", "runs", "workspace-state.json", "audit", "sessions"];
/** What an organisation copy carries. */
export const ORGANISATION_FILE_AREAS = ["facilitator", "runs", "workspace-state.json"];
export const BACKUP_TARGET_KINDS = ["local", "wsl", "devcontainer", "ssh", "node"];
const shortText = z.string().max(500);
const id = z.string().min(1).max(200);
/** A path inside a repository's storage: relative, forward slashes, never `..`. */
export const backupRelativePathSchema = z
    .string()
    .min(1)
    .max(400)
    .refine((p) => !p.startsWith("/") && !p.includes("\\") && !/^[A-Za-z]:/.test(p) && !p.split("/").some((s) => s === ".." || s === "." || s === ""), "a relative path with forward slashes and no '..'");
export const backupFileSchema = z.object({
    path: backupRelativePathSchema,
    size: z.number().int().nonnegative(),
    /** ISO time the file was last written; the newest copy wins when two meet. */
    mtime: z.string().max(40),
    encoding: z.enum(["utf8", "base64"]),
    data: z.string(),
});
export const backupRepositorySchema = z.object({
    /** Unique within the backup (the desktop's id for the repository at the time). */
    key: id,
    name: shortText,
    /** The folder's own name — the last resort when matching it to a folder on another machine. */
    folderName: shortText,
    /** Its id in the project (`.mojoup/project.json` `rootId`, or the desktop's choice). */
    rootId: z.string().max(200).optional(),
    /** What the repository's committed project manifest said. */
    manifest: z.object({ projectId: z.string().max(200).optional(), rootId: z.string().max(200).optional() }).optional(),
    /** `origin`, with any user name and password removed. */
    remoteUrl: z.string().max(2000).optional(),
    branch: z.string().max(300).optional(),
    /** The machine it was on: `wsl` and `Ubuntu-24.04`, `local` and the computer's name. */
    target: z.object({ kind: z.enum(BACKUP_TARGET_KINDS), id: z.string().max(300), name: shortText }),
    /** The path it had there (host-native). */
    path: z.string().max(2000),
    /** That machine's home folder, and the path below it, so `~/src/x` is looked for in a new distro's home. */
    home: z.string().max(2000).optional(),
    pathInHome: z.string().max(2000).optional(),
    /** Whether its workbench data is in the backup: `none` when it had none, `not-reachable` when its machine could not be read. */
    data: z.enum(["included", "none", "not-reachable"]),
    files: z.array(backupFileSchema).max(100_000),
    /** Files left out, and why (too big, over the size limit, transcripts not included). */
    skipped: z.array(z.object({ path: z.string().max(400), reason: shortText })).max(1000).default([]),
    /** One sentence when the data could not be read. */
    note: shortText.optional(),
});
export const backupProjectSchema = z.object({
    /** The desktop's id for the project at the time. */
    id,
    /** `prj_…`, shared with the organisation and every repository's manifest. */
    projectId: z.string().max(200).optional(),
    name: shortText,
    accent: z.string().max(50).optional(),
    /** The project settings layer. */
    settings: z.record(z.string(), z.unknown()).default({}),
    repositories: z.array(backupRepositorySchema).max(500),
});
export const workbenchBackupSchema = z.object({
    kind: z.literal(WORKBENCH_BACKUP_KIND),
    version: z.number().int().positive(),
    /** `local`: a file on this computer. `organisation`: the copy an organisation keeps (text only, small). */
    scope: z.enum(["local", "organisation"]),
    createdAt: z.string().max(40),
    device: z.object({ installId: z.string().max(200).optional(), name: shortText.optional(), platform: z.string().max(50).optional(), appVersion: z.string().max(50).optional() }),
    /** The desktop's settings layers: everywhere, and per machine (keyed by target id). */
    settings: z.object({ global: z.record(z.string(), z.unknown()).default({}), targets: z.record(z.string(), z.record(z.string(), z.unknown())).default({}) }),
    projects: z.array(backupProjectSchema).max(500),
});
/** What a PUT answers, and what a GET adds beside the backup. */
export const workbenchBackupStoredSchema = z.object({
    storedAt: z.string().max(40),
    revision: z.number().int().nonnegative(),
    /** Bytes of the stored body. */
    size: z.number().int().nonnegative(),
});
/** `GET …/summary`: enough to offer "Restore your workbench from <organisation>" without downloading it. */
export const workbenchBackupSummarySchema = workbenchBackupStoredSchema.extend({
    createdAt: z.string().max(40),
    version: z.number().int().positive(),
    device: workbenchBackupSchema.shape.device,
    projects: z.number().int().nonnegative(),
    repositories: z.number().int().nonnegative(),
});
/** Anything → a backup this client can restore, or the reason it is not one. */
export function parseWorkbenchBackup(raw) {
    const kind = raw?.kind;
    if (kind !== WORKBENCH_BACKUP_KIND)
        return { error: "This is not an AI Workbench backup." };
    const version = raw.version;
    if (typeof version === "number" && version > WORKBENCH_BACKUP_VERSION)
        return { error: "This backup was made by a newer AI Workbench. Update, then restore it." };
    const parsed = workbenchBackupSchema.safeParse(raw);
    if (!parsed.success)
        return { error: `The backup is damaged: ${parsed.error.issues[0]?.path.join(".") || "?"} ${parsed.error.issues[0]?.message ?? ""}`.trim() };
    return { backup: parsed.data };
}
/**
 * Settings keys that could carry a credential and so never go into a backup:
 * request headers (an OTLP collector's `Authorization`), and anything named a
 * token, secret, password or private key path. Keys are compared without the
 * `mjuWorkbench.` prefix.
 */
export function isSecretSettingKey(key) {
    const k = key.replace(/^mjuWorkbench\./, "");
    return /(headers|token|secret|password|apikey|api_key|privatekey|keypath)$/i.test(k);
}
/** A settings layer without {@link isSecretSettingKey} keys. */
export function withoutSecretSettings(layer) {
    return Object.fromEntries(Object.entries(layer).filter(([k]) => !isSecretSettingKey(k)));
}
/** A remote URL without its user name and password (`https://user:token@host/x` → `https://host/x`); scp-style `git@host:x` is kept. */
export function redactRemoteUrl(url) {
    const raw = url?.trim();
    if (!raw)
        return undefined;
    return raw.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^@/]*@/i, "$1");
}
//# sourceMappingURL=workbenchBackup.js.map